import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AppErrorDetails } from '@/types/Api'

const isErrorResponse = (value: unknown): value is AppErrorDetails & { statusCode: number } => {
	if (typeof value !== 'object' || value === null) return false
	const candidate = value as Record<string, unknown>
	return Number.isInteger(candidate.statusCode) && typeof candidate.message === 'string' &&
		(candidate.title === undefined || typeof candidate.title === 'string')
}

export const toAppErrorDetails = (error: unknown): AppErrorDetails => {
	if (typeof error !== 'object' || error === null) {
		return { message: 'Nepodařilo se načíst nastavení aplikace.' }
	}

	const source = error as {
		message?: unknown
		apiError?: unknown
		response?: { status?: unknown; data?: unknown }
		config?: { method?: unknown; url?: unknown }
	}
	const body = source.response?.data
	const normalized = isErrorResponse(source.apiError)
		? source.apiError
		: isErrorResponse(body)
			? body
			: undefined
	const statusCode = normalized?.statusCode ?? source.response?.status
	const method = typeof source.config?.method === 'string' ? source.config.method : undefined
	const url = typeof source.config?.url === 'string' ? source.config.url : undefined

	return {
		message: normalized?.message ?? (typeof source.message === 'string'
			? source.message
			: 'Nepodařilo se načíst nastavení aplikace.'),
		...(typeof statusCode === 'number' ? { statusCode } : {}),
		...(normalized?.title ? { title: normalized.title } : {}),
		...(method || url ? { request: { method, url } } : {})
	}
}

const PENDING_ERROR_KEY = 'euc-telemedicine-pending-error'

export const useErrorStore = defineStore('error', () => {
	const currentError = ref<AppErrorDetails | null>(null)

	const setError = (error: AppErrorDetails) => {
		currentError.value = error
	}

	const restorePendingError = () => {
		const serialized = sessionStorage.getItem(PENDING_ERROR_KEY)
		if (!serialized) return

		sessionStorage.removeItem(PENDING_ERROR_KEY)
		try {
			const error: unknown = JSON.parse(serialized)
			if (
				typeof error === 'object' && error !== null &&
				typeof (error as AppErrorDetails).message === 'string'
			) {
				currentError.value = error as AppErrorDetails
			}
		} catch {
			// Ignore invalid or stale session data.
		}
	}

	const setPendingError = (error: AppErrorDetails) => {
		sessionStorage.setItem(PENDING_ERROR_KEY, JSON.stringify(error))
	}

	return { currentError, setError, restorePendingError, setPendingError }
})
